import { useRouter } from 'next/router';
import { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { resetPasswordSchema, ResetPasswordInput } from '@interfaces/auth';
import { Layouts } from '@interfaces/layouts';
import useToastContext from '@contexts/ToastContext/hook';

import { Image } from '@vibTheme/components/_base/Image';
import { Link } from '@vibTheme/components/_base/Link';
import { Button } from '@vibTheme/components/_base/Button';
import { Box, IconButton } from '@mui/material';
import { Typography } from '@vibTheme/components/_base/Typography';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import InputDocument from '@vibTheme/components/_base/Input/InputDocument';
import { resetPassword } from '@api/profile';
import { PageTypes } from '@interfaces/pageType';
import { resolveInternalCallback } from '@utils/resolveInternalCallback';

export default function ResetPassword() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);

  /**
   * A volta ao login devolve o DESTINO que trouxe a pessoa até aqui.
   *
   * Quem chega por um convite e não lembra a senha passa por esta página; com os links literais, o
   * login seguinte terminava em `/` — com o convite pendente e o cookie de capacidade correndo.
   */
  const urlDeLogin = () => {
    const destino = resolveInternalCallback(
      router.query.callbackUrl,
      typeof window === 'undefined' ? '' : window.location.origin,
    );

    return destino
      ? `/auth/signIn?callbackUrl=${encodeURIComponent(destino)}`
      : '/auth/signIn';
  };
  const [passwordRecovered, setPasswordRecovered] = useState(false);
  const toast = useToastContext();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordInput>({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onSubmit: SubmitHandler<ResetPasswordInput> = async (data) => {
    setIsLoading(true);
    try {
      const response = await resetPassword(data);
      const toastStatus = response.success ? 'success' : 'error';
      toast.setContent(response.msg, toastStatus);
      response.success && setPasswordRecovered(true);
    } catch (err) {
      toast.handleError(
        err,
        'Ocorreu um erro inesperado. Por favor, entre em contato com o suporte.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box className="relative flex flex-col items-center justify-center rounded-default shadow-default p-main lg:p-28 text-center w-full max-w-7xl">
      <Link href={urlDeLogin()}>
        <IconButton className="absolute top-8 left-4 text-primary">
          <KeyboardBackspaceIcon fontSize="large" />
        </IconButton>
      </Link>
      <Image
        src="/assets/lock.svg"
        width="120"
        height="120"
        alt="Problemas com o login?"
        priority
      />
      <Typography component="h1" className="pt-lg pb-sm">
        Problemas com Login?
      </Typography>
      <Typography>Entre com seu CPF/CNPJ para redefinir sua senha.</Typography>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-main pt-lg lg:w-[26rem]"
      >
        <InputDocument
          control={control}
          name="document"
          label="CPF/CNPJ"
          placeholder="ex: 123.456.789-00"
          fullWidth
          error={!!errors.document}
          helperText={errors.document?.message}
        />
        <Button type="submit" loading={isLoading} disabled={passwordRecovered}>
          Recuperar
        </Button>
        {passwordRecovered && (
          <Link href={urlDeLogin()}>Voltar para a página de login</Link>
        )}
      </form>
    </Box>
  );
}

ResetPassword.pageType = PageTypes.PUBLIC;
ResetPassword.layout = Layouts.APPBARONLY;
